/**
 * Global API Service with Error Auto-Detection
 * Intercepts all API errors and triggers the ticket system
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store reference to ticket context (will be set by TicketProvider)
let ticketContextRef = null;

export const setTicketContextRef = (contextRef) => {
  ticketContextRef = contextRef;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp for debugging
    config.metadata = { startTime: new Date() };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Extract error details
    const errorMessage = extractErrorMessage(error);
    const errorDetails = extractErrorDetails(error);
    const pageUrl = window.location.href;
    const component = error.config?.url || 'Unknown API endpoint';

    // Log for debugging
    console.error('[API Error]', {
      message: errorMessage,
      status: error.response?.status,
      url: error.config?.url,
      details: errorDetails,
    });

    // Skip auto-popup for certain scenarios
    const skipAutoPopup = shouldSkipAutoPopup(error);

    // Trigger ticket modal if context is available and error is significant
    if (ticketContextRef && !skipAutoPopup) {
      ticketContextRef.openTicketModal({
        errorMessage: errorMessage,
        errorStack: errorDetails,
        pageUrl: pageUrl,
        component: component,
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Extract user-friendly error message
 */
function extractErrorMessage(error) {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Extract technical error details
 */
function extractErrorDetails(error) {
  const details = [];
  
  if (error.response) {
    details.push(`Status: ${error.response.status}`);
    details.push(`URL: ${error.config?.url}`);
    details.push(`Method: ${error.config?.method?.toUpperCase()}`);
    if (error.response.data) {
      details.push(`Response: ${JSON.stringify(error.response.data)}`);
    }
  } else if (error.request) {
    details.push('No response received from server');
    details.push(`URL: ${error.config?.url}`);
  } else {
    details.push(`Error: ${error.message}`);
  }
  
  return details.join('\n');
}

/**
 * Determine if we should skip the auto-popup for this error
 */
function shouldSkipAutoPopup(error) {
  // Skip for 401 (unauthorized) - user needs to login
  if (error.response?.status === 401) {
    return true;
  }
  
  // Skip for 403 (forbidden) - permission issue, not a bug
  if (error.response?.status === 403) {
    return true;
  }
  
  // Skip for ticket-related endpoints (avoid infinite loop)
  if (error.config?.url?.includes('/tickets/')) {
    return true;
  }
  
  // Skip for canceled requests
  if (axios.isCancel(error)) {
    return true;
  }

  // Skip for network errors on health checks
  if (error.config?.url?.includes('/health')) {
    return true;
  }

  return false;
}

export default api;
